import { useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { Highlight } from '@/lib/highlights';

export interface EngagementStats {
  /** Unique reactors (latest reaction per pubkey). */
  reactions: number;
  /** The viewer's own reaction event id, if they reacted (for un-reacting). */
  myReactionId?: string;
  /** Total kind 1111 comments in the thread. */
  comments: number;
  /** Total sats zapped (best-effort parse of receipts). */
  zapSats: number;
  /** Number of zap receipts. */
  zapCount: number;
}

export type EngagementMap = Map<string, EngagementStats>;

const MAX_IDS = 50;

/** Extract the sats amount from a kind 9735 zap receipt. */
function receiptSats(receipt: NostrEvent): number {
  // Preferred: the `amount` tag (millisatoshis).
  const amountTag = receipt.tags.find(([name]) => name === 'amount')?.[1];
  if (amountTag) {
    const msats = Number(amountTag);
    if (Number.isFinite(msats) && msats > 0) return Math.round(msats / 1000);
  }

  // Next: parse the amount out of the BOLT11 invoice HRP.
  const bolt11 = receipt.tags.find(([name]) => name === 'bolt11')?.[1];
  if (bolt11) {
    const match = bolt11.toLowerCase().match(/^ln(?:bc|tb|tbs|bcrt)(\d+)([munp])?/);
    if (match) {
      const amount = Number(match[1]);
      const multiplier =
        match[2] === 'm' ? 1e-3 : match[2] === 'u' ? 1e-6 : match[2] === 'n' ? 1e-9 : match[2] === 'p' ? 1e-12 : 1;
      return Math.round(amount * multiplier * 1e8);
    }
  }

  // Last resort: the amount tag inside the embedded zap request.
  const description = receipt.tags.find(([name]) => name === 'description')?.[1];
  if (description) {
    try {
      const request = JSON.parse(description) as { tags?: string[][] };
      const amount = request.tags?.find(([name]) => name === 'amount')?.[1];
      if (amount) {
        const msats = Number(amount);
        if (Number.isFinite(msats) && msats > 0) return Math.round(msats / 1000);
      }
    } catch {
      // Malformed description — count the zap but not the sats.
    }
  }

  return 0;
}

/**
 * Batch-fetch engagement (reactions, comments, zap receipts) for a list of
 * highlights in a single relay round-trip, grouped per highlight id.
 */
export function useEngagement(highlights: Highlight[]) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  const ids = useMemo(
    () => highlights.map((h) => h.event.id).slice(0, MAX_IDS),
    [highlights],
  );
  const idsKey = ids.join(',');

  return useQuery({
    queryKey: ['nostr', 'engagement', idsKey, user?.pubkey ?? ''],
    queryFn: async (): Promise<EngagementMap> => {
      if (ids.length === 0) return new Map();

      const events = await nostr.query(
        [
          { kinds: [7], '#e': ids },
          { kinds: [9735], '#e': ids },
          { kinds: [1111], '#E': ids },
        ],
        { signal: AbortSignal.timeout(6000) },
      );

      interface MutableStats {
        reactors: Map<string, { id: string; created_at: number }>; // pubkey → latest reaction
        comments: number;
        zapSats: number;
        zapCount: number;
      }

      const stats = new Map<string, MutableStats>();
      const entry = (id: string): MutableStats => {
        let e = stats.get(id);
        if (!e) {
          e = { reactors: new Map(), comments: 0, zapSats: 0, zapCount: 0 };
          stats.set(id, e);
        }
        return e;
      };

      const idSet = new Set(ids);

      for (const event of events) {
        if (event.kind === 7) {
          const target = event.tags.find(([name]) => name === 'e')?.[1];
          if (!target || !idSet.has(target)) continue;
          // '-' is a retract convention — ignore it.
          if (event.content.trim() === '-') continue;
          const e = entry(target);
          const existing = e.reactors.get(event.pubkey);
          // Keep the latest reaction per pubkey.
          if (!existing || event.created_at > existing.created_at) {
            e.reactors.set(event.pubkey, { id: event.id, created_at: event.created_at });
          }
        } else if (event.kind === 9735) {
          const target = event.tags.find(([name]) => name === 'e')?.[1];
          if (!target || !idSet.has(target)) continue;
          const e = entry(target);
          e.zapSats += receiptSats(event);
          e.zapCount += 1;
        } else if (event.kind === 1111) {
          const target = event.tags.find(([name]) => name === 'E')?.[1];
          if (!target || !idSet.has(target)) continue;
          entry(target).comments += 1;
        }
      }

      const map: EngagementMap = new Map();
      for (const [id, e] of stats) {
        map.set(id, {
          reactions: e.reactors.size,
          myReactionId: user ? e.reactors.get(user.pubkey)?.id : undefined,
          comments: e.comments,
          zapSats: e.zapSats,
          zapCount: e.zapCount,
        });
      }
      return map;
    },
    enabled: ids.length > 0,
    staleTime: 30 * 1000,
  });
}
